import type { ParameterValueSaveData } from "@/composables/collectionItem/params";
import { useParametersApi } from "@/composables/collectionItem/params";
import type { HistoryItemDetail, HistoryListItem } from "@/services/aiIdentification/aiIdentificationTypes";
import { CategoryId } from "@/services/collection/categories";
import type { CollectionItem, CreateCollectionItemData } from "@/services/collection/item";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item";
import { setImagesFromAiIdentification } from "@/services/collection/item/gallery";
import * as PostHog from "@/services/analytics/postHog";
import { factoryImage, type Image } from "@/services/images";
import type { PriceNumeric, PriceRange } from "@/services/prices";
import { isNumericPrice } from "@/services/prices";
import type { Parameters } from "@/services/collection/parameters";

export const createCollectionItemFromIdentification = async (identification: HistoryItemDetail): Promise<CollectionItem> => {
  const { saveSelectedValue: saveParameterValue } = useParametersApi();

  // 1) Create item itself
  const categoryId = identification.categoryId;
  let creationData: CreateCollectionItemData;
  if (categoryId === CategoryId.LegoSets || categoryId === CategoryId.LegoMinifigures) {
    creationData = {
      categoryId,
      collectibleId: identification.catalogueItem.id,
      set: identification.catalogueItem.id,
      minifigure: identification.catalogueItem.id,
      userCollectionListName: "",
    };
  } else {
    creationData = {
      categoryId,
      customName: identification.name,
      userCollectionListName: "",
    };
  }
  const item = await createCollectionItem(creationData);

  const promises = [];

  // 2) Add parameters
  identification.parameters.map((param) => {
    const saveData: ParameterValueSaveData = {
      itemId: item.id,
      parameterId: param.parameterId,
      value: param.valueId,
      type: "predefined",
    };
    promises.push(saveParameterValue(saveData));
  });

  // 3) Add images
  promises.push(setImagesFromAiIdentification(item, identification));

  // 5) Set identification origin
  const aiIdentificationSourceId = identification.id;
  promises.push(updateCollectionItem(item.id, { aiIdentificationSourceId }));

  // 6) Save AI-generated description
  const publicNote = identification.description ?? "";
  promises.push(updateCollectionItem(item.id, { publicNote }));

  await Promise.all(promises);
  PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
  return item;
};

export const SupportedCategoryIds = [CategoryId.SportsCards, CategoryId.Pokemon];

/** @todo rework into composable?? */
export const getCoverImage = (item: HistoryListItem): Image | undefined => {
  const img = item.images.length > 0 ? item.images[0] : undefined;
  if (img) {
    return factoryImage(0 /* fake ID */, img.imageUrl, item.name);
  } else {
    return undefined;
  }
};

export const getImages = (item: HistoryListItem | HistoryItemDetail): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const getParamValue = (param: Parameters.Parameter, item: HistoryItemDetail): string | undefined => item.parameters.find((p) => p.parameterId === param.id)?.value;

export const hasPrice = (item: HistoryListItem | HistoryItemDetail): item is (HistoryItemDetail | HistoryItemDetail) & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);

export const getPriceRange = (item: HistoryListItem | HistoryItemDetail): PriceRange => {
  if (!isNumericPrice(item.priceFrom) || !isNumericPrice(item.priceTo)) {
    throw new Error("Cannot get price range for item without price.");
  }
  if (item.priceFrom.currencyId !== item.priceTo.currencyId) {
    throw new Error("Price range currencyId mismatch.");
  }

  return {
    from: item.priceFrom.price,
    to: item.priceTo.price,
    currencyId: item.priceFrom.currencyId,
  };
};
